import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Bouton à icône uniquement — `aria-label` devient obligatoire pour l'accessibilité */
  iconOnly?: boolean;
  fullWidth?: boolean;
  children: ReactNode;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary:   'bg-accent-500 text-zinc-950 hover:bg-accent-400 active:bg-accent-600 font-medium',
  secondary: 'bg-zinc-800 text-zinc-100 hover:bg-zinc-700 active:bg-zinc-700 border border-zinc-700',
  ghost:     'text-zinc-300 hover:bg-zinc-800 active:bg-zinc-800',
  danger:    'bg-rose-900/40 text-rose-300 hover:bg-rose-900/60 border border-rose-900',
  success:   'bg-emerald-900/30 text-emerald-300 hover:bg-emerald-900/50 border border-emerald-900',
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'min-h-tap min-w-tap px-3 py-2 text-sm',
  md: 'min-h-tap min-w-tap px-4 py-2.5 text-sm',
  lg: 'min-h-tap min-w-tap px-5 py-3 text-base',
};

const iconOnlyOverride: Record<ButtonSize, string> = {
  sm: 'min-h-tap min-w-tap p-2',
  md: 'min-h-tap min-w-tap p-3',
  lg: 'min-h-tap min-w-tap p-3.5',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', iconOnly = false, fullWidth = false, className = '', children, type = 'button', ...rest },
  ref,
) {
  if (iconOnly && !rest['aria-label']) {
    if (typeof console !== 'undefined') {
      console.warn('[Button] iconOnly requiert un aria-label pour l\'accessibilité.');
    }
  }
  return (
    <button
      ref={ref}
      type={type}
      className={[
        'inline-flex items-center justify-center gap-2 rounded-xl transition-colors select-none',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400/60',
        'disabled:opacity-40 disabled:cursor-not-allowed disabled:active:bg-current',
        iconOnly ? iconOnlyOverride[size] : sizeStyles[size],
        variantStyles[variant],
        fullWidth ? 'w-full' : '',
        className,
      ].filter(Boolean).join(' ')}
      {...rest}
    >
      {children}
    </button>
  );
});
